import { useEffect, useRef, useState } from 'react';
import { getAllMyRecords } from '../../services/userLotsService';
import { RECORD_KIND } from '../utils/recordCards';

/**
 * Sin `shape`: la API ya manda `boundary` / `shape_local` en las cards sin portada;
 * con `shape` también lo manda en las que tienen foto, donde solo sirve si la imagen
 * no carga. Se ahorra ese peso en cada página.
 */
export const RECORDS_INCLUDE = ['commercial_summary'];
export const RECORDS_SORT = 'updated_desc';

/** Un resultado completo se reutiliza sin pedir de nuevo durante este tiempo. */
export const RECORDS_CACHE_TTL_MS = 2 * 60 * 1000;
const CACHE_MAX_ENTRIES = 8;

/**
 * Caché en memoria por (owner, kind, list_id, sort):
 * `{ cards, complete, at, reloadKey }`. `reloadKey` es el que tenía el panel cuando
 * se cargó (con la carga legacy lista); si al reabrir es otro, se vuelve a pedir.
 */
const cache = new Map();

export const recordsCacheKey = ({ ownerUserId, kind, listId, sort }) => (
  [ownerUserId || '', (kind || []).join(','), listId || '', sort || ''].join('|')
);

const cacheSet = (key, entry) => {
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > CACHE_MAX_ENTRIES) cache.delete(cache.keys().next().value);
};

/** Borra la caché (tests, o tras una escritura que cambie las cards). */
export function clearApiRecordCardsCache() {
  cache.clear();
}

/** Cards nuevas primero y, mientras revalida, las anteriores que todavía no llegaron. */
const overlayCards = (fresh, stale) => {
  if (!stale?.length) return fresh;
  const seen = new Set(fresh.map((card) => card?.record_key).filter(Boolean));
  return [...fresh, ...stale.filter((card) => !card?.record_key || !seen.has(card.record_key))];
};

const OFF = { cards: null, status: 'off', httpStatus: null, complete: false, loaded: 0, total: null };

/**
 * Fuente `api` de las cards por registro: `getmyrecords` (todas las páginas,
 * `include=commercial_summary`), con carga progresiva.
 *
 * - `enabled`: fuente `api` y vista por registro. Apagado → `cards: null`.
 * - La primera página se publica en cuanto llega (`status: 'loading'`,
 *   `complete: false`) y cada página siguiente se suma en segundo plano. El panel
 *   fusiona por `record_key` con las cards legacy (`mergeApiRecords`): el orden y
 *   el universo siguen siendo los legacy, así que sumar páginas no reordena.
 * - Arranca sin esperar a la carga legacy (`ready`), en paralelo con `getmylots`.
 * - `ready` / `reloadKey`: con la carga legacy lista, si `reloadKey` cambia
 *   (p. ej. cantidad de lotes) se vuelve a pedir conservando las cards actuales
 *   (stale-while-revalidate). El primer `reloadKey` «listo» no dispara otra carga
 *   y `reloadKey: null` se ignora (filtros legacy que achican el universo: la API
 *   ya trae todo, no hace falta volver a pedir).
 * - Caché en memoria por (owner, kind, list_id, sort): al reabrir el panel se
 *   pintan al instante; si está completa, tiene menos de `RECORDS_CACHE_TTL_MS` y el
 *   `reloadKey` coincide, no se pide nada.
 * - `AbortController`: se aborta al cambiar de lista / espacio / tipos o al desmontar.
 * - Falla en la primera página (404 sin ruta, 401, 400, 5xx, red) → `cards: null`,
 *   `status: 'error'`: el panel sigue con legacy. Si falla una página posterior se
 *   conservan las cards ya recibidas (el resto queda legacy) y no se cachea.
 *
 * @param {{ enabled: boolean, ready?: boolean, listId?: string|null, ownerUserId?: string|null, includeCad?: boolean, reloadKey?: unknown }} options
 * @returns {{ cards: object[]|null, status: 'off'|'loading'|'ok'|'error', httpStatus: number|null, complete: boolean, loaded: number, total: number|null }}
 */
export function useApiRecordCards({
  enabled,
  ready = true,
  listId = null,
  ownerUserId = null,
  includeCad = true,
  reloadKey = null,
}) {
  const kind = listId || !includeCad
    ? [RECORD_KIND.LOT, RECORD_KIND.UNIT]
    : [RECORD_KIND.LOT, RECORD_KIND.UNIT, RECORD_KIND.CAD];
  const key = recordsCacheKey({ ownerUserId, kind, listId, sort: RECORDS_SORT });

  const [state, setState] = useState(() => {
    const hit = enabled ? cache.get(key) : null;
    return hit
      ? { cards: hit.cards, status: hit.complete ? 'ok' : 'loading', httpStatus: 200, complete: hit.complete, loaded: hit.cards.length, total: hit.total ?? null }
      : OFF;
  });
  const [generation, setGeneration] = useState(0);
  const keyRef = useRef(null);
  const cardsRef = useRef(state.cards);
  /** `reloadKey` visto con la carga legacy lista (base para detectar cambios). */
  const readyReloadKeyRef = useRef(null);

  // Carga: al habilitar, al cambiar de alcance (key) o al pedir revalidar (generation).
  useEffect(() => {
    if (!enabled) {
      keyRef.current = null;
      cardsRef.current = null;
      readyReloadKeyRef.current = null;
      setState(OFF);
      return undefined;
    }
    const scopeChanged = keyRef.current !== key;
    keyRef.current = key;
    const hit = cache.get(key);
    let stale = scopeChanged ? null : cardsRef.current;
    if (scopeChanged) {
      readyReloadKeyRef.current = hit?.reloadKey ?? null;
      if (hit) {
        stale = hit.cards;
        cardsRef.current = hit.cards;
        setState({ cards: hit.cards, status: hit.complete ? 'ok' : 'loading', httpStatus: 200, complete: hit.complete, loaded: hit.cards.length, total: hit.total ?? null });
      } else {
        // Otra lista / espacio sin caché: las cards anteriores no sirven.
        cardsRef.current = null;
        setState({ ...OFF, status: 'loading' });
      }
      const fresh = hit && hit.complete && Date.now() - hit.at < RECORDS_CACHE_TTL_MS;
      if (fresh) {
        return undefined;
      }
    }

    const controller = new AbortController();
    let alive = true;
    if (stale) setState((prev) => ({ ...prev, status: 'loading', complete: false }));

    const publish = (cards, extra) => {
      cardsRef.current = cards;
      setState((prev) => ({ ...prev, cards, httpStatus: 200, loaded: cards.length, ...extra }));
    };

    getAllMyRecords({
      kind,
      listId: listId || undefined,
      ownerUserId: ownerUserId || undefined,
      sort: RECORDS_SORT,
      include: RECORDS_INCLUDE,
      signal: controller.signal,
      onPage: (_page, { data, total, done }) => {
        if (!alive || done) return;
        publish(overlayCards(data, stale), { status: 'loading', complete: false, total: total ?? null });
      },
    })
      .then((res) => {
        if (!alive) return;
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn('[records] getmyrecords falló; sigue la fuente legacy', res.status, res.code || res.error || '');
          if (res.data?.length) {
            // Falló una página posterior: quedan las recibidas (el resto, legacy).
            publish(overlayCards(res.data, stale), { status: 'error', complete: false, httpStatus: res.status ?? null });
            return;
          }
          cardsRef.current = stale;
          setState(stale
            ? (prev) => ({ ...prev, status: 'error', complete: false, httpStatus: res.status ?? null })
            : { ...OFF, status: 'error', httpStatus: res.status ?? null });
          return;
        }
        cacheSet(key, { cards: res.data, complete: true, at: Date.now(), total: res.total ?? null, reloadKey: readyReloadKeyRef.current });
        publish(res.data, { status: 'ok', complete: true, total: res.total ?? null, httpStatus: res.status ?? 200 });
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[records] getmyrecords falló; sigue la fuente legacy', err?.message || err);
        cardsRef.current = stale;
        setState(stale
          ? (prev) => ({ ...prev, status: 'error', complete: false, httpStatus: null })
          : { ...OFF, status: 'error' });
      });
    return () => {
      alive = false;
      controller.abort();
    };
    // `kind` se deriva de `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, generation]);

  // Revalidar cuando cambian los datos legacy (con la carga legacy lista).
  useEffect(() => {
    // `null`: el panel no puede comparar (p. ej. filtros legacy activos); no cuenta.
    if (!enabled || !ready || reloadKey == null) return;
    const normalized = String(reloadKey);
    const previous = readyReloadKeyRef.current;
    readyReloadKeyRef.current = normalized;
    const entry = cache.get(key);
    if (entry && entry.reloadKey == null) entry.reloadKey = normalized;
    if (previous == null || previous === normalized) return;
    if (entry) entry.at = 0;
    setGeneration((n) => n + 1);
  }, [enabled, ready, reloadKey, key]);

  return state;
}
